import React, { useState, useEffect } from 'react';
import { useTheme } from '../lib/theme';
import { Badge, MiniGauge, OutcomeBadge, TimingBadge, BAND_COLORS, ARCHETYPE_COLORS, URGENCY_COLORS } from './ui';

var API_URL = "https://supra-intelligence-worker.data-f9f.workers.dev";

function FilterButton({ active, label, onClick, color }) {
  return React.createElement('button', { onClick: onClick, style: {
    padding: '6px 12px', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif", borderRadius: 4, transition: 'all 0.15s',
    background: active ? (color ? color + '20' : 'rgba(129,140,248,0.15)') : 'rgba(255,255,255,0.02)',
    color: active ? (color || '#818CF8') : 'rgba(255,255,255,0.3)',
    border: '1px solid ' + (active ? (color ? color + '40' : 'rgba(129,140,248,0.3)') : 'rgba(255,255,255,0.04)'),
  }}, label);
}

function PipelineBar({ leads }) {
  var showed = leads.filter(function(l) { return l.actual_showed === 'TRUE'; }).length;
  var noShow = leads.filter(function(l) { return l.actual_showed === 'FALSE'; }).length;
  var closed = leads.filter(function(l) { return l.actual_closed === 'TRUE'; }).length;
  var pending = leads.filter(function(l) { return !l.actual_showed || l.actual_showed === 'PENDING'; }).length;
  var total = leads.length;
  var revenue = leads.reduce(function(s, l) { return s + (l.deal_amount || 0); }, 0);

  return React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 } },
    [
      { label: 'Total Leads', value: total, color: '#818CF8' },
      { label: 'Showed', value: showed, color: '#4ADE80' },
      { label: 'No Show', value: noShow, color: '#EF4444' },
      { label: 'Closed', value: closed, color: '#E8A838' },
      { label: 'Revenue', value: '$' + revenue.toLocaleString(), color: '#38BDF8' },
    ].map(function(m) {
      return React.createElement('div', { key: m.label, style: {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 8, padding: '12px 14px', textAlign: 'center',
      }},
        React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: m.color, fontFamily: "'DM Mono', monospace" } }, m.value),
        React.createElement('div', { style: { fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 } }, m.label)
      );
    })
  );
}

export default function LeadList({ onSelect }) {
  var t = useTheme();
  var leadsState = useState([]); var leads = leadsState[0]; var setLeads = leadsState[1];
  var loadingState = useState(true); var loading = loadingState[0]; var setLoading = loadingState[1];
  var searchState = useState(''); var search = searchState[0]; var setSearch = searchState[1];
  var filterState = useState('all'); var filter = filterState[0]; var setFilter = filterState[1];
  var repFilterState = useState('all'); var repFilter = repFilterState[0]; var setRepFilter = repFilterState[1];
  var outcomeFilterState = useState('all'); var outcomeFilter = outcomeFilterState[0]; var setOutcomeFilter = outcomeFilterState[1];

  useEffect(function() {
    fetch(API_URL + '/api/leads')
      .then(function(r) { return r.json(); })
      .then(function(d) { setLeads(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, []);

  if (loading) {
    return React.createElement('div', { style: { textAlign: 'center', padding: 60, color: t.c.textFaint } }, 'Loading leads...');
  }

  // Get unique reps
  var reps = []; var repSet = {};
  leads.forEach(function(l) { if (l.rep_name && !repSet[l.rep_name]) { repSet[l.rep_name] = true; reps.push(l.rep_name); } });
  reps.sort();

  // Filter leads
  var filtered = leads.filter(function(l) {
    if (search && !((l.name || '').toLowerCase().includes(search.toLowerCase()) || (l.id || '').includes(search))) return false;
    if (filter === 'p1') return l.priority === 'P1';
    if (filter === 'urgent') return l.intervention_urgency === 'immediate' || l.intervention_urgency === 'within_24h';
    if (filter === 'high_ghost') return l.ghost_risk >= 0.5;
    if (filter === 'committed') return l.commitment_band === 'COMMITTED' || l.commitment_band === 'ENGAGED';
    if (repFilter !== 'all' && l.rep_name !== repFilter) return false;
    if (outcomeFilter === 'showed' && l.actual_showed !== 'TRUE') return false;
    if (outcomeFilter === 'no_show' && l.actual_showed !== 'FALSE') return false;
    if (outcomeFilter === 'closed' && l.actual_closed !== 'TRUE') return false;
    if (outcomeFilter === 'pending' && l.actual_showed && l.actual_showed !== 'PENDING') return false;
    return true;
  });

  return React.createElement('div', { style: { maxWidth: 740, margin: '0 auto' } },
    // Pipeline summary
    React.createElement(PipelineBar, { leads: leads }),

    // Search
    React.createElement('input', {
      type: 'text', placeholder: 'Search leads...', value: search,
      onChange: function(e) { setSearch(e.target.value); },
      style: {
        width: '100%', padding: '10px 14px', borderRadius: 6, border: '1px solid ' + t.c.borderCard,
        background: t.c.bgCard, color: t.c.text, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
        outline: 'none', boxSizing: 'border-box', marginBottom: 12,
      }
    }),

    // Filter bars
    React.createElement('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 } },
      React.createElement(FilterButton, { active: filter === 'all', label: 'All (' + leads.length + ')', onClick: function() { setFilter('all'); } }),
      React.createElement(FilterButton, { active: filter === 'p1', label: 'P1 Priority', onClick: function() { setFilter('p1'); }, color: '#EF4444' }),
      React.createElement(FilterButton, { active: filter === 'urgent', label: 'Urgent', onClick: function() { setFilter('urgent'); }, color: '#E8A838' }),
      React.createElement(FilterButton, { active: filter === 'high_ghost', label: 'High Ghost Risk', onClick: function() { setFilter('high_ghost'); }, color: '#EF4444' }),
      React.createElement(FilterButton, { active: filter === 'committed', label: 'Engaged+', onClick: function() { setFilter('committed'); }, color: '#4ADE80' })
    ),

    // Outcome filters
    React.createElement('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 } },
      React.createElement(FilterButton, { active: outcomeFilter === 'all', label: 'All Outcomes', onClick: function() { setOutcomeFilter('all'); } }),
      React.createElement(FilterButton, { active: outcomeFilter === 'showed', label: 'Showed', onClick: function() { setOutcomeFilter('showed'); }, color: '#4ADE80' }),
      React.createElement(FilterButton, { active: outcomeFilter === 'no_show', label: 'No Show', onClick: function() { setOutcomeFilter('no_show'); }, color: '#EF4444' }),
      React.createElement(FilterButton, { active: outcomeFilter === 'closed', label: 'Closed Won', onClick: function() { setOutcomeFilter('closed'); }, color: '#E8A838' }),
      React.createElement(FilterButton, { active: outcomeFilter === 'pending', label: 'Pending', onClick: function() { setOutcomeFilter('pending'); } })
    ),

    // Rep filter
    React.createElement('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 } },
      React.createElement(FilterButton, { active: repFilter === 'all', label: 'All Reps', onClick: function() { setRepFilter('all'); } }),
      reps.map(function(r) {
        return React.createElement(FilterButton, { key: r, active: repFilter === r, label: r, onClick: function() { setRepFilter(r); } });
      })
    ),

    // Count
    React.createElement('div', { style: { fontSize: 11, color: t.c.textFaint, marginBottom: 12 } }, filtered.length + ' leads'),

    // Lead cards
    filtered.map(function(lead) {
      var showColor = (lead.show_probability_current || lead.show_probability || 0) >= 0.7 ? '#4ADE80'
        : (lead.show_probability_current || lead.show_probability || 0) >= 0.5 ? '#E8A838' : '#EF4444';
      var bandColor = BAND_COLORS[lead.commitment_band] || '#E8A838';
      var archColor = ARCHETYPE_COLORS[lead.archetype] || 'rgba(255,255,255,0.3)';
      var urgColor = URGENCY_COLORS[lead.intervention_urgency] || null;
      var hasOutcome = lead.actual_showed && lead.actual_showed !== 'PENDING';

      return React.createElement('div', {
        key: lead.id,
        onClick: function() { onSelect(lead); },
        style: {
          background: t.c.bgCard, border: '1px solid ' + t.c.borderCard, borderRadius: 10,
          padding: '14px 16px', marginBottom: 8, cursor: 'pointer', transition: 'all 0.15s',
          borderLeft: urgColor ? '3px solid ' + urgColor : '3px solid transparent',
        },
      },
        // Row 1: Name, badges, gauge
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1 } },
            React.createElement('span', { style: { fontSize: 14, fontWeight: 700, color: t.c.text } }, lead.name),
            React.createElement(Badge, { color: bandColor }, (lead.commitment_band || 'UNKNOWN').replace(/_/g, ' ')),
            React.createElement(Badge, { color: '#818CF8' }, lead.priority),
            lead.lifecycle_state && React.createElement(Badge, { color: 'rgba(255,255,255,0.3)' }, (lead.lifecycle_state || '').replace(/_/g, ' ')),
            hasOutcome && React.createElement(OutcomeBadge, { showed: lead.actual_showed, closed: lead.actual_closed, dealAmount: lead.deal_amount })
          ),
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            lead.show_decayed && React.createElement('span', { style: { fontSize: 9, color: '#EF4444', fontFamily: "'DM Mono', monospace" } }, '\u2193'),
            React.createElement(MiniGauge, { value: lead.show_probability_current || lead.show_probability || 0, color: showColor })
          )
        ),

        // Row 2: Meta info
        React.createElement('div', { style: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: t.c.textMuted } },
          React.createElement('span', null, lead.rep_name),
          React.createElement(Badge, { color: archColor }, (lead.archetype || 'unknown').replace(/_/g, ' ')),
          lead.expected_revenue && React.createElement('span', { style: { color: '#E8A838', fontFamily: "'DM Mono', monospace" } }, '$' + Math.round(lead.expected_revenue).toLocaleString() + ' exp'),
          lead.timing && lead.timing.speed_to_lead_rating && React.createElement(TimingBadge, { rating: lead.timing.speed_to_lead_rating }),
          lead.lead_gender && React.createElement('span', { style: { color: t.c.textFaint } }, lead.lead_gender),
          lead.meeting_date && React.createElement('span', { style: { color: t.c.textFaint } }, lead.meeting_date)
        ),

        // Row 3: Reasoning & timing risk (if available)
        (lead.reasoning && lead.reasoning.primary_driver) && React.createElement('div', { style: { marginTop: 6, fontSize: 10, color: t.c.textFaint, fontStyle: 'italic' } },
          'Show driver: ' + lead.reasoning.primary_driver,
          lead.show_decay_reason && React.createElement('span', { style: { color: '#EF4444', marginLeft: 8 } }, '\u26a0 ' + lead.show_decay_reason)
        )
      );
    })
  );
}
